import { useState } from 'react';
import { Button, Input } from '@roger/ui-components';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { selectVisibleChannelIds } from '../../store/slices/chatSlice';
import { useCareTeamChannel, useInbox } from '../../hooks/chat/useChatQueries';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { InboxRow } from './InboxRow';

/** The conversation rows, pinned care team and search. The caller draws the header. */
export function ChatInboxList({
  density,
  onOpen,
  onNewMessage,
}: {
  density: 'default' | 'compact';
  onOpen: (channelId: string) => void;
  onNewMessage: () => void;
}) {
  const location = useLocation();
  const currentPatientId =
    location.pathname.match(/^\/patients\/([^/]+)/)?.[1] ?? null;
  const { data, isLoading, error } = useInbox();
  const chatContextEpisodeId = useStore(state => state.chatContextEpisodeId);
  const shownIds = useStore(useShallow(selectVisibleChannelIds));
  const [search, setSearch] = useState('');
  // `useCareTeamChannel` is gated on the episode id and on the chat flag, and
  // answers 404/403 as `null`, so no request fires without a chart on screen
  // and a non-member pins nothing.
  const { data: contextChannel } = useCareTeamChannel(
    chatContextEpisodeId ?? undefined
  );
  const rows = data ?? [];
  // Keep a retained episode from a chart remount from pinning a row for a
  // different patient, or a page that is not a patient chart.
  const isCurrentPatientChannel =
    currentPatientId !== null &&
    contextChannel?.patient_id === currentPatientId;
  const pinnedRow =
    isCurrentPatientChannel && contextChannel
      ? rows.find(r => r.channel_id === contextChannel.id)
      : undefined;
  const pinnedId = pinnedRow?.channel_id ?? null;
  const orderedRows = pinnedRow
    ? [pinnedRow, ...rows.filter(r => r.channel_id !== pinnedRow.channel_id)]
    : rows;
  const query = search.trim().toLowerCase();
  const visibleRows = query
    ? orderedRows.filter(row => row.title.toLowerCase().includes(query))
    : orderedRows;
  // Announcement channels the caller can post in but has not joined, listed
  // after the caller's own rows.
  const ownRows = visibleRows.filter(row => row.is_member !== false);
  const otherRows = visibleRows.filter(row => row.is_member === false);
  const renderRow = (row: (typeof rows)[number]) => (
    <InboxRow
      key={row.channel_id}
      row={row}
      density={density}
      onOpen={onOpen}
      highlighted={row.channel_id === pinnedId}
      active={shownIds.includes(row.channel_id)}
    />
  );

  // The flag went away after load; the tray unmounts on the next /auth/me
  // refresh, and until then the dock says so rather than claiming there are no
  // conversations. Any other failure with no rows to show gets the same copy,
  // not the empty state.
  if (getHttpStatus(error) === 403 || (error && !data)) {
    return (
      <p className='px-6 py-8 text-center text-sm text-muted-foreground'>
        Messages are not available.
      </p>
    );
  }
  if (!isLoading && rows.length === 0) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 px-6 text-center'>
        <p className='text-sm text-muted-foreground'>No conversations yet</p>
        <Button size='sm' onClick={onNewMessage}>
          New message
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className='px-3 py-2'>
        <Input
          type='search'
          inputSize='sm'
          aria-label='Search messages'
          data-chat-search=''
          placeholder='Search'
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto'>
        {visibleRows.length === 0 && query ? (
          <p className='px-6 py-8 text-center text-sm text-muted-foreground'>
            No matches
          </p>
        ) : (
          <>
            {ownRows.map(renderRow)}
            {otherRows.length > 0 && (
              <>
                <h3 className='px-3 pb-1 pt-3 text-xs font-medium text-muted-foreground'>
                  Other announcements
                </h3>
                {otherRows.map(renderRow)}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
